"use client";

import {
  ArrowRight,
  FileQuestion,
  HelpCircle,
  Home,
  MessageSquare,
  Search,
} from "lucide-react";
import Link from "next/link";
import { useLocale } from "next-intl";
import { Button } from "@/components/ui/button";
import { Link as I18nLink } from "@/i18n/routing";
import { getPublicStateCopy } from "@/lib/public-state-copy";

export function PublicNotFound() {
  const copy = getPublicStateCopy(useLocale()).notFound;

  const links = [
    { href: "/pricing", label: copy.pricing, Icon: Search, external: false },
    {
      href: "/support",
      label: copy.support,
      Icon: MessageSquare,
      external: false,
    },
    {
      href: "https://docs.sendbot.co/",
      label: copy.docs,
      Icon: FileQuestion,
      external: true,
    },
    { href: "/tools", label: copy.tools, Icon: ArrowRight, external: false },
  ] as const;

  return (
    <div className="flex min-h-[70vh] flex-col items-center justify-center px-4 py-20 text-center">
      <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10 text-primary-readable">
        <FileQuestion className="h-8 w-8" />
      </div>

      <h1 className="text-4xl font-bold tracking-tight md:text-5xl">
        {copy.title}
      </h1>
      <p className="mt-4 max-w-xl text-lg text-muted-foreground">
        {copy.description}
      </p>

      <div className="mt-8 flex flex-col gap-3 sm:flex-row">
        <Button asChild size="lg" className="rounded-full px-6">
          <I18nLink href="/">
            <Home className="mr-2 h-4 w-4" />
            {copy.home}
          </I18nLink>
        </Button>
        <Button
          asChild
          variant="outline"
          size="lg"
          className="rounded-full px-6"
        >
          <I18nLink href="/support">
            <HelpCircle className="mr-2 h-4 w-4" />
            {copy.help}
          </I18nLink>
        </Button>
      </div>

      <section className="mt-12 w-full max-w-2xl rounded-3xl border border-border bg-card/55 p-6 text-left">
        <h2 className="mb-4 text-lg font-semibold">{copy.popular}</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          {links.map(({ href, label, Icon, external }) => {
            const content = (
              <>
                <div className="mr-3 flex h-9 w-9 items-center justify-center rounded-xl border border-primary/20 bg-primary/10 text-primary-readable">
                  <Icon className="h-4 w-4" />
                </div>
                <span className="font-medium">{label}</span>
              </>
            );

            const className =
              "flex items-center rounded-2xl border border-transparent p-3 transition-colors hover:border-border hover:bg-background/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

            return external ? (
              <Link
                key={href}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className={className}
              >
                {content}
              </Link>
            ) : (
              <I18nLink key={href} href={href} className={className}>
                {content}
              </I18nLink>
            );
          })}
        </div>
      </section>
    </div>
  );
}
